import React, { useEffect, useState } from 'react';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from './firebaseConfig';
import { isRunningInCapacitor, subscribeToTier } from './subscriptionBridge';

const db = getFirestore(app);

const paymentTiers = [
  {
    id: 'local',
    name: 'Local (Botswana Citizens)',
    priceLabel: 'P100',
    description: 'Access for Botswana citizens - 2 weeks membership',
  },
  {
    id: 'sadc',
    name: 'SADC Citizens',
    priceLabel: 'P200',
    description: 'Access for SADC region citizens - 2 weeks membership',
  },
  {
    id: 'global',
    name: 'Global Citizens',
    priceLabel: 'P380',
    description: 'Access for international users - 2 weeks membership',
  },
];

const WEB_SUBSCRIBE_MESSAGE =
  'Payment will be processed through the App Store / Google Play after you install the mobile app';

const PaymentPage = ({ user, onSignOut }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [webSubscribeModalOpen, setWebSubscribeModalOpen] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setUserProfile(docSnap.data());
        }
        // No banner on missing doc or errors: web tier chooser stays calm; subscribe uses in-app dialog only.
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };

    fetchUserProfile();
  }, [user]);

  const membershipActive =
    userProfile?.paymentStatus === 'paid' &&
    userProfile?.membershipExpiry &&
    new Date() < userProfile.membershipExpiry.toDate();

  const handleSubscribe = async (tierId) => {
    if (isRunningInCapacitor()) {
      await subscribeToTier(tierId);
      return;
    }
    // Web: in-page dialog (embedded browsers often block window.alert). No navigation, no payment.
    setWebSubscribeModalOpen(true);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 font-sans">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md relative">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          {membershipActive ? 'Membership Active' : 'Choose Your Membership Tier'}
        </h2>

        {membershipActive ? (
          <p className="text-green-700 mb-6 text-center">Your membership is active. You can continue using the app.</p>
        ) : (
          <>
            <p className="text-center text-gray-700 mb-4">
              <strong>Payment will be processed through App Store / Google Play</strong>
            </p>

            <div className="space-y-4 mb-6">
              {paymentTiers.map((tier) => {
                return (
                  <div
                    key={tier.id}
                    className="p-4 border border-gray-300 rounded-md transition duration-200 ease-in-out hover:border-blue-400"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h3 className="text-xl font-semibold text-gray-800">{tier.name}</h3>
                        <p className="text-2xl font-bold text-gray-900 mt-2">{tier.priceLabel}</p>
                        <p className="text-sm text-gray-600 mt-1">{tier.description}</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleSubscribe(tier.id)}
                        className="shrink-0 h-10 px-4 bg-green-600 text-white font-semibold rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75 transition duration-200 ease-in-out"
                        title="Subscribe (opens info on web; purchase in mobile app)"
                      >
                        Subscribe
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {!user && (
              <p className="text-sm text-gray-600 text-center mb-6">Please log in to continue.</p>
            )}
          </>
        )}

        <button
          onClick={onSignOut}
          className="w-full py-2 px-4 bg-gray-800 text-white font-semibold rounded-md shadow-md hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75 transition duration-200 ease-in-out"
        >
          Sign Out
        </button>

        {webSubscribeModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="web-subscribe-title"
          >
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <h3 id="web-subscribe-title" className="text-lg font-semibold text-gray-900 mb-2">
                Subscribe
              </h3>
              <p className="text-gray-700 text-sm mb-4">{WEB_SUBSCRIBE_MESSAGE}</p>
              <button
                type="button"
                onClick={() => setWebSubscribeModalOpen(false)}
                className="w-full py-2 px-4 bg-green-600 text-white font-semibold rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;